import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { isFunction, isUndefined } from 'lodash';
import {
  getPreference,
  isQuotaError,
  migrateLegacyLocalStorageKey,
  setPreference,
  subscribePreferenceChanges,
} from 'services/storage/preferencesIndexedDB';
import { Logger } from '../../utilities';

/**
 * @description A hook to manage UI preference state persisted in IndexedDB (with legacy localStorage migration).
 * @param key - The key to store the value
 * @param initialValue - The initial value when no persisted value exists
 * @returns A tuple containing the current value and a function to update the value
 */
export const useLocalStorage = <T = any>(key: string, initialValue?: T) => {
  const [storedValue, setStoredValue] = useState<T>(initialValue as T);
  const storedValueRef = useRef(storedValue);
  storedValueRef.current = storedValue;
  // Track the latest initialValue so the effect can reset to it without being
  // listed as a dependency (avoids re-running load on every render).
  const initialValueRef = useRef(initialValue);
  initialValueRef.current = initialValue;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        await migrateLegacyLocalStorageKey(key);
        const value = await getPreference<T>(key);
        if (!cancelled) {
          // Explicitly reset to initialValue when the key has no stored entry so
          // a key change (e.g. valuations_column_visibility_${sectionId}) does
          // not inherit the previous key's state.
          setStoredValue(value !== undefined ? value : (initialValueRef.current as T));
        }
      } catch (error) {
        Logger.debug(`Error reading preference key "${key}":`, error);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => subscribePreferenceChanges(({ key: changedKey, value }) => {
    if (changedKey === key) {
      // Update the ref synchronously so getCurrentValue() is not stale during the
      // window between the BroadcastChannel message and the React re-render commit.
      storedValueRef.current = value as T;
      setStoredValue(value as T);
    }
  }), [key]);

  const setValue = useCallback((value: T | ((prevValue: T) => T)) => {
    setStoredValue(prevStoredValue => {
      const valueToStore = isFunction(value) ? value(prevStoredValue as T) : value;

      if (!isUndefined(valueToStore)) {
        setPreference(key, valueToStore).catch(error => {
          if (isQuotaError(error)) {
            Logger.warn(`Quota exceeded saving preference key "${key}"`);
          } else {
            Logger.warn(`Error saving preference key "${key}":`, error);
          }
          Sentry.captureException(error, {
            extra: { preferenceKey: key },
          });
        });

        return valueToStore;
      }

      return prevStoredValue;
    });
  }, [key]);

  const getCurrentValue = useCallback((): T => storedValueRef.current as T, []);

  return [storedValue, setValue, getCurrentValue] as const;
};

export default useLocalStorage;
